import { useEffect, useRef, useState, type ReactNode } from "react";
import { Iphone, Win } from "./Device";

/**
 * What HireOS is, told as the page scrolls: every call and text in one place,
 * new jobs and changes kept in the rental system, with the receptionist there for when
 * the team can't get to a call or a text. The steps pass on the left while the screen on the right
 * stays put and changes to the step in the middle of the window. On a phone
 * there is no room beside them, so each step carries its own screen under it,
 * and that screen is the app on a phone, as a phone's visitor would use it:
 * the desk's would be too small there to read. Every screen is a screenshot
 * of the app, never a mock-up.
 */
const STEPS: { title: string; text: ReactNode; phone: ReactNode; shot: ReactNode }[] = [
  {
    title: "One list for the whole team",
    text: "Calls, texts and voicemails land in one place, each under the customer, on the web or the mobile app.",
    phone: <Iphone src="/shots/m-list.webp" alt="The Phone page on a phone: today's calls and texts, each under the customer" />,
    shot: (
      <Win
        src="/shots/d-phone.webp"
        alt="The Phone page on a desk: today's calls and texts down the left, with Hannah Reid's conversation open"
      />
    ),
  },
  {
    title: "New jobs and changes, kept in your rental system",
    text: (
      <>
        Enquiries become draft jobs. Changes by call or text wait on the job for your team to
        accept.{" "}
        <a className="ilink" href="/integrations">
          See integrations
        </a>
      </>
    ),
    // The job itself, in Current RMS: HireOS's notes on it, the call that
    // asked for a change logged as an activity, the items, and the Draft
    // state. A frame of the first demo's walkthrough, the account's own logo
    // and the support widget painted out. On a phone it stays the desk's
    // window, as nobody runs Current RMS from their pocket.
    phone: <RmsWin />,
    shot: <RmsWin />,
  },
  {
    title: "Every call written up",
    text: (
      <>
        Calls are recorded and written out, with a summary on top. Nobody types one up again.{" "}
        <a className="ilink" href="#film">
          Watch a call
        </a>
      </>
    ),
    phone: <Iphone src="/shots/m-job-open.webp" alt="A call opened on a phone: the summary, the recording and the transcript" />,
    shot: (
      <Win
        src="/shots/d-job-open.webp"
        alt="A call opened on its enquiry: the summary, the recording and the transcript, line by line"
      />
    ),
  },
  {
    title: "When your team can't get to it",
    text: (
      <>
        Nobody free? Our AI receptionist answers the call or text and passes on the rest.{" "}
        <a className="ilink" href="#live">
          Call it live now
        </a>
      </>
    ),
    phone: <Iphone src="/shots/m-texts.webp" alt="Tom Gallagher on a phone: the receptionist took his call, then answered his two questions by text" />,
    shot: (
      <Win
        src="/shots/d-texts.webp"
        alt="Tom Gallagher in HireOS: the receptionist took his call, then answered his two questions by text"
      />
    ),
  },
];

function RmsWin() {
  return (
    <Win
      src="/shots/d-rms.webp"
      size={[1568, 887]}
      title={{ name: "Current RMS", mark: "/logos/current-rms-mark.png" }}
      alt="The job drafted in Current RMS: HireOS's notes on it, the customer's call logged as an activity, the items priced, and the job in Draft"
    />
  );
}

/** Each step's panel colour; the panel beside the steps fades between them. */
const TONES = ["sage", "heather", "ochre", "sky"];

export function Every() {
  const [on, setOn] = useState(0);
  const beats = useRef<(HTMLDivElement | null)[]>([]);

  // The step nearest the middle of the window is the one on screen. Measured
  // on every scroll rather than watched for crossing the middle, which a
  // quick fling can jump clean over.
  useEffect(() => {
    let raf = 0;
    const pick = () => {
      raf = 0;
      const mid = innerHeight / 2;
      let best = 0;
      let dist = Infinity;
      beats.current.forEach((el, i) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        const d = Math.abs(r.top + r.height / 2 - mid);
        if (d < dist) [best, dist] = [i, d];
      });
      setOn(best);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(pick);
    };
    pick();
    addEventListener("scroll", onScroll, { passive: true });
    addEventListener("resize", onScroll);
    return () => {
      removeEventListener("scroll", onScroll);
      removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section className="every" id="how">
      <div className="col head">
        <h2>How it works.</h2>
      </div>
      <div className="col scrolly">
        <div className="beats">
          {STEPS.map((s, i) => (
            <div
              className={`beat${i === on ? " on" : ""}`}
              key={s.title}
              ref={(el) => {
                beats.current[i] = el;
              }}
            >
              <span className="num">0{i + 1}</span>
              <h3>{s.title}</h3>
              <p>{s.text}</p>
              <div className={`fpane inline ${TONES[i]}`}>{s.phone}</div>
            </div>
          ))}
        </div>
        <div className="stick" aria-hidden="true">
          <div className={`fpane ${TONES[on]}`}>
            {STEPS.map((s, i) => (
              <div className={`frame${i === on ? " on" : ""}`} key={s.title}>
                {s.shot}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
